import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { axiosTasklist } from '@/utils/lib/axiosTasklist';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import useCompanyProfileCompletionState from './completion.state';

export interface ICompanyField {
  value?: any;
  is_published?: boolean;
}

export interface ICompanyProfile {
  id?: number | string;
  tenant_id?: number | string;
  [key: string]: ICompanyField | number | string | undefined;
}

interface CompanyState {
  companyProfile: ICompanyProfile | null;
  isLoading: boolean;
  error: unknown;

  createCompanyInformation: (tenantId: string | number, payload: Partial<ICompanyProfile>) => Promise<void>;
  getCompanyInformation: (tenantId: string | number) => Promise<any>;
  updateCompanyField: (tenantId: string | number, field: string, value: any, isPublished?: boolean, showToast?: boolean) => Promise<void>;
  updateCompanyInfo: (tenantId: string | number, payload: Partial<ICompanyProfile>) => Promise<void>;
  uploadCompanyMedia: (file: File, modelId: string | number, name?: string) => Promise<any>;
  safeUpdateCompanyField: (
    tenantId: string | number,
    field: string,
    value: any,
    isPublished?: boolean,
  ) => Promise<void>;
  clearCompanyProfile: () => void;
}

const useCompanyState = create<CompanyState>(set => ({
  companyProfile: null,
  isLoading: false,
  error: null,

  clearCompanyProfile: () => set({ companyProfile: null }),

  createCompanyInformation: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}`;
      const { data } = await axiosInstance.post(url, payload);
      
      set({ companyProfile: data.data, isLoading: false });
      
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();

      toast.success('Company information created successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getCompanyInformation: async tenantId => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}`;
      const { data } = await axiosInstance.get(url);

      let parsedFiles: any[] = [];
      const kb = data?.knowledge_base;
      if (typeof kb === 'string') {
        try {
          const files = JSON.parse(kb);
          if (Array.isArray(files)) {
            parsedFiles = files.map(f => ({
              ...f,
              name: f.filename,
              url: f.url.startsWith('http') ? f.url : `${env.apiTasklistBaseUrl}${f.url}`,
            }));
          }
        } catch (e) {
          console.error(e);
        }
      }

      set({
        companyProfile: {
          ...data,
          parsedFiles,
        },
        isLoading: false,
      });

      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      return { ...data, parsedFiles };
    } catch (error: any) {
      if (error.response?.status === 404) {
        set({ companyProfile: null, isLoading: false });
        return null;
      }

      set({ error, isLoading: false });
      errorHandler(error);
      throw error;
    }
  },

  updateCompanyInfo: async (tenantId: string | number, payload: Partial<ICompanyProfile>) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}`;
      const { data } = await axiosInstance.put(url, payload);
      set(state => ({
        companyProfile: { ...state.companyProfile, ...data },
        isLoading: false,
      }));
      toast.success('Company information updated successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  updateCompanyField: async (tenantId, field, value, isPublished = true, showToast = true) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}/detail`;
      const payload = {
        field,
        value,
        [`is_published_${field}`]: isPublished,
      };

      const { data } = await axiosInstance.put(url, payload);

      set(state => ({
        companyProfile: { ...state.companyProfile, ...data },
        isLoading: false,
      }));

      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();

      if (showToast) {
        toast.success(`${field.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} updated successfully`);
      }
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  uploadCompanyMedia: async (file, modelId, name = 'Company Media') => {
    set({ isLoading: true, error: null });
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('model_type', 'org_profile');
      formData.append('model_id', modelId.toString());
      formData.append('collection_name', 'attachments');
      formData.append('name', name);

      const { data } = await axiosTasklist.post('/api/v1/compro/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      set({ isLoading: false });
      return data;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
      throw error;
    }
  },

  safeUpdateCompanyField: async (tenantId, field, value, isPublished = true) => {
    set({ isLoading: true, error: null });
    try {
      try {
        await axiosInstance.get(`${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}`);
      } catch (err: any) {
        if (err.response?.status === 404) {
          console.warn(`[SAFE UPDATE] Company profile belum ada, buat baru...`);
          const payload = { [field]: value, [`is_published_${field}`]: isPublished };
          const createRes = await axiosInstance.post(
            `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}`,
            payload,
          );
          set({ companyProfile: createRes.data.data, isLoading: false });
          toast.success('Company profile created automatically!');
          return;
        }
        throw err;
      }

      const payload = {
        field,
        value,
        [`is_published_${field}`]: isPublished,
      };

      const { data } = await axiosInstance.put(
        `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}/detail`,
        payload,
      );
      set(state => ({
        companyProfile: { ...state.companyProfile, ...data },
        isLoading: false,
      }));

      toast.success(`${field.replace('_', ' ')} updated successfully`);
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default useCompanyState;
